import { Container } from 'pixi.js'
import type { SymbolPool } from '../../slotbase/SymbolPool'
import type { ReelStrip } from './ReelStrip'
import type { SymbolView } from './SymbolView'

// 單一轉輪（一欄）。以連續位移捲動模擬旋轉，停輪時保證最終內容正確對位。
// 內容由 ReelStrip 提供（符號或倍數幣）；對應 Unity SlotReel + Slot001_MultiplierReel。
export class ReelView extends Container {
  private readonly rowCount: number
  private readonly strip: ReelStrip
  private readonly highlightMiddle: boolean
  private readonly sprites: SymbolView[] = [] // 長度 rowCount+1；index 0 為頂部緩衝
  private cellH = 0 // 列高（垂直滾動步長）；欄寬由 ReelsView 以容器位置決定
  private scroll = 0
  private speed = 0
  private spinning = false
  private stopping = false
  private feed: number[] = []
  private finalIds: number[] = []
  private stoppedResolve: (() => void) | null = null

  constructor(rowCount: number, pool: SymbolPool, strip: ReelStrip, highlightMiddle = false) {
    super()
    this.rowCount = rowCount
    this.strip = strip
    this.highlightMiddle = highlightMiddle
    for (let i = 0; i < rowCount + 1; i++) {
      const sprite = pool.obtain()
      sprite.setTexture(strip.texture(strip.randomId()))
      this.sprites.push(sprite)
      this.addChild(sprite)
    }
  }

  /** 目前停輪後盤面內容（finalIds 副本）— 診斷用。 */
  get currentIds(): readonly number[] {
    return this.finalIds
  }

  /** 是否正在旋轉（含停輪減速中）。 */
  get isSpinning(): boolean {
    return this.spinning
  }

  setCell(width: number, height: number): void {
    this.cellH = height
    for (const sprite of this.sprites) sprite.setCell(width, height)
    this.reposition()
  }

  /** 不經動畫直接顯示一組內容。 */
  setStatic(ids: readonly number[]): void {
    this.finalIds = [...ids]
    this.scroll = 0
    this.spinning = false
    this.stopping = false
    this.assignFinal()
    this.reposition()
    this.applyMiddleHighlight()
  }

  startSpin(speed: number): void {
    this.spinning = true
    this.stopping = false
    this.speed = speed
  }

  /** 請求停輪並對位到 final；回傳停輪完成的 Promise。 */
  requestStop(final: readonly number[]): Promise<void> {
    this.finalIds = [...final]
    this.feed = [...final]
    this.stopping = true
    return new Promise<void>((resolve) => {
      this.stoppedResolve = resolve
    })
  }

  update(deltaSeconds: number): void {
    if (!this.spinning) return
    this.scroll += this.speed * deltaSeconds
    while (this.scroll >= this.cellH) {
      this.scroll -= this.cellH
      this.onCross()
      if (!this.spinning) break
    }
    this.reposition()
  }

  dimAll(dim: boolean): void {
    for (let r = 0; r < this.rowCount; r++) this.sprites[r + 1]?.setDimmed(dim)
  }

  /** 標記中獎格：高亮且取消變暗。 */
  markWin(row: number): void {
    const sprite = this.sprites[row + 1]
    if (sprite === undefined) return
    sprite.setHighlight(true)
    sprite.setDimmed(false)
  }

  clearEffects(): void {
    for (const sprite of this.sprites) {
      sprite.setHighlight(false)
      sprite.setDimmed(false)
    }
    this.applyMiddleHighlight()
  }

  private onCross(): void {
    const bottom = this.sprites.pop()
    if (bottom === undefined) return
    this.sprites.unshift(bottom)

    if (this.stopping) {
      const next = this.feed.pop()
      if (next === undefined) {
        this.settle()
        return
      }
      bottom.setTexture(this.strip.texture(next))
      if (this.feed.length === 0) this.settle()
    } else {
      bottom.setTexture(this.strip.texture(this.strip.randomId()))
    }
  }

  private settle(): void {
    this.spinning = false
    this.stopping = false
    this.scroll = 0
    this.assignFinal()
    this.reposition()
    this.applyMiddleHighlight()
    const resolve = this.stoppedResolve
    this.stoppedResolve = null
    resolve?.()
  }

  private assignFinal(): void {
    for (let r = 0; r < this.rowCount; r++) {
      const id = this.finalIds[r] ?? this.strip.randomId()
      this.sprites[r + 1]?.setTexture(this.strip.texture(id))
    }
    this.sprites[0]?.setTexture(this.strip.texture(this.strip.randomId()))
  }

  private applyMiddleHighlight(): void {
    if (!this.highlightMiddle) return
    this.sprites[Math.floor(this.rowCount / 2) + 1]?.setHighlight(true)
  }

  private reposition(): void {
    for (let i = 0; i < this.sprites.length; i++) {
      this.sprites[i].position.set(0, (i - 1) * this.cellH + this.scroll)
    }
  }
}
